import type { AudioRecCalibrationProfile, RecognitionFrame } from '../types';
import { midiToName } from '../util/note';
import type {
  LocalAudioBridgeEventDetail,
  LocalAudioGradeEventDetail,
} from 'Utils/LocalAudioSessionEvents';
import { serializeWaveformSnapshot } from './waveformCapture';

export const SIGHT_READING_TEST_MAX_FRAMES = 12_000;

export type SightReadingTestFrame = {
  capturedAtMs: number;
  audioTimeSec: number;
  exercisePlaying: boolean;
  expectationKey: string;
  expectedMidiNotes: number[];
  recognizedMidiNotes: number[];
  onsetMidiNotes: number[];
  rms: number;
  minRms: number;
  noiseFloor: number;
  residualEnergy: number | null;
  recognitionMode: string;
  waveform?: number[];
};

export type SightReadingExpectationResult = {
  index: number;
  startedAtMs: number;
  endedAtMs: number;
  durationMs: number;
  expectedMidiNotes: number[];
  expectedNoteNames: string[];
  frameCount: number;
  aboveGateFrameCount: number;
  attempted: boolean;
  requiresRearticulation: boolean;
  observedExpectedOnsetMidiNotes: number[];
  recognizedExactly: boolean;
  firstSignalAtMs: number | null;
  firstExactAtMs: number | null;
  scoreToDecisionLatencyMs: number | null;
  signalToDecisionLatencyMs: number | null;
};

export type SightReadingRecognitionEvaluation = {
  frameCount: number;
  scoredFrameCount: number;
  aboveGateScoredFrameCount: number;
  exactFrameCount: number;
  exactFrameRate: number;
  precision: number;
  recall: number;
  f1: number;
  aboveGatePrecision: number;
  aboveGateRecall: number;
  aboveGateF1: number;
  expectationCount: number;
  attemptedExpectationCount: number;
  recognizedExpectationCount: number;
  missedAfterSignalCount: number;
  noSignalExpectationCount: number;
  recognitionRate: number;
  attemptedRecognitionRate: number;
  medianScoreToDecisionLatencyMs: number | null;
  p95ScoreToDecisionLatencyMs: number | null;
  medianSignalToDecisionLatencyMs: number | null;
  p95SignalToDecisionLatencyMs: number | null;
  falsePositiveMidiCounts: Record<string, number>;
  falseNegativeMidiCounts: Record<string, number>;
  expectations: SightReadingExpectationResult[];
};

export type SightReadingTestReport = {
  schemaVersion: 1;
  source: 'sight-reading-trainer';
  createdAtIso: string;
  startedAtIso: string;
  endedAtIso: string;
  durationMs: number;
  frameCount: number;
  evaluation: SightReadingRecognitionEvaluation;
  calibrationProfile: AudioRecCalibrationProfile | null;
  metadata: Record<string, unknown>;
  bridgeEvents: LocalAudioBridgeEventDetail[];
  gradeEvents: LocalAudioGradeEventDetail[];
  frames: SightReadingTestFrame[];
};

type SightReadingTestReportOptions = {
  startedAtMs: number;
  endedAtMs?: number;
  calibrationProfile: AudioRecCalibrationProfile | null;
  metadata?: Record<string, unknown>;
  bridgeEvents?: LocalAudioBridgeEventDetail[];
  gradeEvents?: LocalAudioGradeEventDetail[];
};

export type SightReadingExpectedMidiSnapshot = {
  midiNotes: number[];
  expectationKey?: string;
};

type FrameScore = {
  exact: boolean;
  truePositiveCount: number;
  falsePositiveMidiNotes: number[];
  falseNegativeMidiNotes: number[];
};

const normalizeMidiNotes = (notes: number[]) =>
  Array.from(new Set(notes.map((note) => Math.round(note))))
    .filter((note) => note >= 0 && note <= 127)
    .sort((left, right) => left - right);

const expectedSignature = (frame: SightReadingTestFrame) =>
  frame.expectationKey || `midi:${normalizeMidiNotes(frame.expectedMidiNotes).join(',')}`;

const isAboveGate = (frame: SightReadingTestFrame) =>
  Number.isFinite(frame.rms) &&
  Number.isFinite(frame.minRms) &&
  frame.minRms > 0 &&
  frame.rms >= Math.max(0, frame.minRms);

const scoreRecognizedNotes = (
  expectedMidiNotes: number[],
  recognizedMidiNotes: number[],
): FrameScore => {
  const expected = new Set(normalizeMidiNotes(expectedMidiNotes));
  const recognized = new Set(normalizeMidiNotes(recognizedMidiNotes));
  const truePositiveCount = Array.from(recognized).filter((midi) => expected.has(midi)).length;
  const falsePositiveMidiNotes = Array.from(recognized).filter((midi) => !expected.has(midi));
  const falseNegativeMidiNotes = Array.from(expected).filter((midi) => !recognized.has(midi));

  return {
    exact: falsePositiveMidiNotes.length === 0 && falseNegativeMidiNotes.length === 0,
    truePositiveCount,
    falsePositiveMidiNotes,
    falseNegativeMidiNotes,
  };
};

const midiNotesEqual = (left: number[], right: number[]) => {
  const normalizedLeft = normalizeMidiNotes(left);
  const normalizedRight = normalizeMidiNotes(right);
  return (
    normalizedLeft.length === normalizedRight.length &&
    normalizedLeft.every((midi, index) => midi === normalizedRight[index])
  );
};

const ratio = (numerator: number, denominator: number, emptyValue = 0) =>
  denominator > 0 ? numerator / denominator : emptyValue;

const f1 = (precision: number, recall: number) =>
  precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;

const percentile = (values: number[], percentileValue: number): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((left, right) => left - right);
  const rank = Math.max(0, Math.ceil(percentileValue * sorted.length) - 1);
  return sorted[Math.min(rank, sorted.length - 1)];
};

const incrementMidiCounts = (counts: Map<number, number>, midiNotes: number[]) => {
  midiNotes.forEach((midi) => counts.set(midi, (counts.get(midi) ?? 0) + 1));
};

const serializeMidiCounts = (counts: Map<number, number>) =>
  Object.fromEntries(
    Array.from(counts.entries())
      .sort(([left], [right]) => left - right)
      .map(([midi, count]) => [`${midiToName(midi)} (${midi})`, count]),
  );

const buildExpectationResults = (
  frames: SightReadingTestFrame[],
): SightReadingExpectationResult[] => {
  const results: SightReadingExpectationResult[] = [];
  let activeFrames: SightReadingTestFrame[] = [];
  let activeSignature = '';

  const finishActiveExpectation = () => {
    if (activeFrames.length === 0) return;

    const firstFrame = activeFrames[0];
    const lastFrame = activeFrames[activeFrames.length - 1];
    const expectedMidiNotes = normalizeMidiNotes(firstFrame.expectedMidiNotes);
    const firstSignalFrame = activeFrames.find(isAboveGate) ?? null;
    const previousExpectation = results[results.length - 1];
    const requiresRearticulation =
      previousExpectation != null &&
      midiNotesEqual(previousExpectation.expectedMidiNotes, expectedMidiNotes);
    const observedExpectedOnsetMidiNotes = new Set<number>();
    let firstExactFrame: SightReadingTestFrame | null = null;

    for (const frame of activeFrames) {
      frame.onsetMidiNotes.forEach((midi) => {
        if (expectedMidiNotes.includes(midi)) {
          observedExpectedOnsetMidiNotes.add(midi);
        }
      });
      const hasRequiredOnsets =
        !requiresRearticulation ||
        expectedMidiNotes.every((midi) => observedExpectedOnsetMidiNotes.has(midi));
      if (
        hasRequiredOnsets &&
        scoreRecognizedNotes(expectedMidiNotes, frame.recognizedMidiNotes).exact
      ) {
        firstExactFrame = frame;
        break;
      }
    }

    results.push({
      index: results.length,
      startedAtMs: firstFrame.capturedAtMs,
      endedAtMs: lastFrame.capturedAtMs,
      durationMs: Math.max(0, lastFrame.capturedAtMs - firstFrame.capturedAtMs),
      expectedMidiNotes,
      expectedNoteNames: expectedMidiNotes.map(midiToName),
      frameCount: activeFrames.length,
      aboveGateFrameCount: activeFrames.filter(isAboveGate).length,
      attempted: firstSignalFrame != null,
      requiresRearticulation,
      observedExpectedOnsetMidiNotes: Array.from(observedExpectedOnsetMidiNotes).sort(
        (left, right) => left - right,
      ),
      recognizedExactly: firstExactFrame != null,
      firstSignalAtMs: firstSignalFrame?.capturedAtMs ?? null,
      firstExactAtMs: firstExactFrame?.capturedAtMs ?? null,
      scoreToDecisionLatencyMs: firstExactFrame
        ? Math.max(0, firstExactFrame.capturedAtMs - firstFrame.capturedAtMs)
        : null,
      signalToDecisionLatencyMs:
        firstSignalFrame && firstExactFrame
          ? Math.max(0, firstExactFrame.capturedAtMs - firstSignalFrame.capturedAtMs)
          : null,
    });

    activeFrames = [];
    activeSignature = '';
  };

  frames.forEach((frame) => {
    const signature =
      frame.exercisePlaying && frame.expectedMidiNotes.length > 0 ? expectedSignature(frame) : '';

    if (!signature) {
      finishActiveExpectation();
      return;
    }

    if (activeFrames.length > 0 && signature !== activeSignature) {
      finishActiveExpectation();
    }

    if (activeFrames.length === 0) {
      activeSignature = signature;
    }
    activeFrames.push(frame);
  });

  finishActiveExpectation();
  return results;
};

export const evaluateSightReadingRecognition = (
  frames: SightReadingTestFrame[],
): SightReadingRecognitionEvaluation => {
  const normalizedFrames = frames.map((frame) => ({
    ...frame,
    expectedMidiNotes: normalizeMidiNotes(frame.expectedMidiNotes),
    recognizedMidiNotes: normalizeMidiNotes(frame.recognizedMidiNotes),
    onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes),
  }));
  const scoredFrames = normalizedFrames.filter(
    (frame) => frame.exercisePlaying && frame.expectedMidiNotes.length > 0,
  );
  const aboveGateScoredFrames = scoredFrames.filter(isAboveGate);
  const falsePositiveMidiCounts = new Map<number, number>();
  const falseNegativeMidiCounts = new Map<number, number>();

  const aggregate = (framesToScore: SightReadingTestFrame[]) => {
    if (framesToScore.length === 0) {
      return {
        exactFrameCount: 0,
        precision: 0,
        recall: 0,
        f1: 0,
      };
    }

    let truePositiveCount = 0;
    let falsePositiveCount = 0;
    let falseNegativeCount = 0;
    let exactFrameCount = 0;

    framesToScore.forEach((frame) => {
      const score = scoreRecognizedNotes(frame.expectedMidiNotes, frame.recognizedMidiNotes);
      truePositiveCount += score.truePositiveCount;
      falsePositiveCount += score.falsePositiveMidiNotes.length;
      falseNegativeCount += score.falseNegativeMidiNotes.length;
      if (score.exact) exactFrameCount += 1;

      if (framesToScore === scoredFrames) {
        incrementMidiCounts(falsePositiveMidiCounts, score.falsePositiveMidiNotes);
        incrementMidiCounts(falseNegativeMidiCounts, score.falseNegativeMidiNotes);
      }
    });

    const precision = ratio(
      truePositiveCount,
      truePositiveCount + falsePositiveCount,
      falseNegativeCount === 0 ? 1 : 0,
    );
    const recall = ratio(
      truePositiveCount,
      truePositiveCount + falseNegativeCount,
      falsePositiveCount === 0 ? 1 : 0,
    );

    return {
      exactFrameCount,
      precision,
      recall,
      f1: f1(precision, recall),
    };
  };

  const allFrameScores = aggregate(scoredFrames);
  const aboveGateScores = aggregate(aboveGateScoredFrames);
  const expectations = buildExpectationResults(normalizedFrames);
  const attemptedExpectations = expectations.filter((expectation) => expectation.attempted);
  const recognizedExpectations = expectations.filter(
    (expectation) => expectation.recognizedExactly,
  );
  const scoreToDecisionLatencies = recognizedExpectations
    .map((expectation) => expectation.scoreToDecisionLatencyMs)
    .filter((latency): latency is number => latency != null);
  const signalToDecisionLatencies = recognizedExpectations
    .map((expectation) => expectation.signalToDecisionLatencyMs)
    .filter((latency): latency is number => latency != null);

  return {
    frameCount: normalizedFrames.length,
    scoredFrameCount: scoredFrames.length,
    aboveGateScoredFrameCount: aboveGateScoredFrames.length,
    exactFrameCount: allFrameScores.exactFrameCount,
    exactFrameRate: ratio(allFrameScores.exactFrameCount, scoredFrames.length),
    precision: allFrameScores.precision,
    recall: allFrameScores.recall,
    f1: allFrameScores.f1,
    aboveGatePrecision: aboveGateScores.precision,
    aboveGateRecall: aboveGateScores.recall,
    aboveGateF1: aboveGateScores.f1,
    expectationCount: expectations.length,
    attemptedExpectationCount: attemptedExpectations.length,
    recognizedExpectationCount: recognizedExpectations.length,
    missedAfterSignalCount: attemptedExpectations.filter(
      (expectation) => !expectation.recognizedExactly,
    ).length,
    noSignalExpectationCount: expectations.filter((expectation) => !expectation.attempted).length,
    recognitionRate: ratio(recognizedExpectations.length, expectations.length),
    attemptedRecognitionRate: ratio(
      recognizedExpectations.filter((expectation) => expectation.attempted).length,
      attemptedExpectations.length,
      0,
    ),
    medianScoreToDecisionLatencyMs: percentile(scoreToDecisionLatencies, 0.5),
    p95ScoreToDecisionLatencyMs: percentile(scoreToDecisionLatencies, 0.95),
    medianSignalToDecisionLatencyMs: percentile(signalToDecisionLatencies, 0.5),
    p95SignalToDecisionLatencyMs: percentile(signalToDecisionLatencies, 0.95),
    falsePositiveMidiCounts: serializeMidiCounts(falsePositiveMidiCounts),
    falseNegativeMidiCounts: serializeMidiCounts(falseNegativeMidiCounts),
    expectations,
  };
};

export const buildSightReadingTestFrame = (
  frame: RecognitionFrame,
  expected: SightReadingExpectedMidiSnapshot,
  exercisePlaying: boolean,
  capturedAtMs = Date.now(),
  captureWaveform = false,
): SightReadingTestFrame => {
  const waveform = captureWaveform ? serializeWaveformSnapshot(frame.waveform) : undefined;
  return {
    capturedAtMs,
    audioTimeSec: frame.timeSec,
    exercisePlaying,
    expectationKey: expected.expectationKey ?? '',
    expectedMidiNotes: normalizeMidiNotes(expected.midiNotes),
    recognizedMidiNotes: normalizeMidiNotes(frame.midiNotes ?? []),
    onsetMidiNotes: normalizeMidiNotes(frame.onsetMidiNotes ?? []),
    rms: frame.rms ?? 0,
    minRms: frame.minRms ?? 0,
    noiseFloor: frame.noiseFloor ?? 0,
    residualEnergy: frame.residualEnergy ?? null,
    recognitionMode: frame.recognitionMode ?? 'default',
    ...(waveform ? { waveform } : {}),
  };
};

export const buildSightReadingTestReport = (
  frames: SightReadingTestFrame[],
  options: SightReadingTestReportOptions,
): SightReadingTestReport => {
  const endedAtMs = options.endedAtMs ?? Date.now();
  return {
    schemaVersion: 1,
    source: 'sight-reading-trainer',
    createdAtIso: new Date(endedAtMs).toISOString(),
    startedAtIso: new Date(options.startedAtMs).toISOString(),
    endedAtIso: new Date(endedAtMs).toISOString(),
    durationMs: Math.max(0, endedAtMs - options.startedAtMs),
    frameCount: frames.length,
    evaluation: evaluateSightReadingRecognition(frames),
    calibrationProfile: options.calibrationProfile,
    metadata: options.metadata ?? {},
    bridgeEvents: options.bridgeEvents ?? [],
    gradeEvents: options.gradeEvents ?? [],
    frames,
  };
};
